/**
 * BICEP module — BMS Innovation Centre and Entrepreneurship Park
 *
 * bicepApi   — typed client for every BICEP Worker action. Method names map
 *              1:1 to backend/worker.js actions; ids are sent camelCase and
 *              record fields snake_case, exactly as the Worker reads them.
 * useBicep() — hook: is BICEP enabled, and which role does the caller hold?
 *
 * Transport (base URL, institute header, session handling) is shared with the
 * core client in lib/api.js.
 */
import { createContext, useContext, useState, useEffect } from "react";
import { useInstitute } from "@/lib/institute";
import { useAuth } from "@/lib/auth";
import { apiGet, apiPost, ApiError, setToken, setStoredUser, getInstitute } from "@/lib/api";

// The Worker answers HTTP 200 with { success:false, message } for business
// errors (and authError:true for a dead session). Surface both as exceptions so
// no page silently renders an empty list on a failed request.
function unwrap(data) {
  if (data && data.success === false) {
    if (data.authError) {
      // A denied *role* is not an expired session; only clear on "Login required".
      if (/login required|not authenticated/i.test(data.message || "")) {
        setToken(null); setStoredUser(null);
        throw new ApiError("Session expired. Please sign in again.", 401, data);
      }
      throw new ApiError(data.message || "Access denied", 403, data);
    }
    throw new ApiError(data.message || "Request failed", 400, data);
  }
  return data;
}
const _get  = async (params) => unwrap(await apiGet(params));
const _post = async (body)   => unwrap(await apiPost(body));

/** Public pages have no sign-in, so there may be no institute picked yet. BICEP is
 *  a BMSIT module: fall back to it (an explicit ?inst= or a stored choice always wins). */
export function ensureInstitute() {
  try {
    const q = new URLSearchParams(window.location.search).get("inst");
    if (q) { localStorage.setItem("events.institute", q); return q; }
    if (!getInstitute()) localStorage.setItem("events.institute", "bmsit");
  } catch { /* storage unavailable */ }
  return getInstitute();
}

export const bicepApi = {
  // ── Public landing ────────────────────────────────────────────────────────
  getLanding:              () => { ensureInstitute(); return _get({ action: "bicepGetLanding" }); },
  // ── Info, dashboard & inbox ───────────────────────────────────────────────
  getInfo:                 () => _get({ action: "bicepGetInfo" }),
  getDashboard:            () => _get({ action: "bicepGetDashboard" }),
  getMyNotifications:      () => _get({ action: "bicepGetMyNotifications" }),
  markNotificationsRead:   (ids) => _post({ action: "bicepMarkNotificationsRead", ids }),

  // ── Applications ──────────────────────────────────────────────────────────
  apply:                   (fields) => _post({ action: "bicepApply", ...fields }),
  getMyApplications:       () => _get({ action: "bicepGetMyApplications" }),
  withdrawApplication:     (applicationId) => _post({ action: "bicepWithdrawApplication", applicationId }),
  submitPreIncubation:     (form) => _post({ action: "bicepSubmitPreIncubation", form }),
  getApplicationFull:      (applicationId) => _get({ action: "bicepGetApplicationFull", applicationId }),
  adminSaveOfficeUse:      (applicationId, fields) => _post({ action: "bicepAdminSaveOfficeUse", applicationId, ...fields }),
  // ── Shared profile (Events + BICEP) & overview ────────────────────────────
  getMyProfile:            () => _get({ action: "getMyProfile" }),
  saveMyProfile:           (fields) => _post({ action: "saveMyProfile", ...fields }),
  search:                  (q) => { ensureInstitute(); return _get({ action: "bicepSearch", q }); },
  adminSuggestMentors:     (requestId) => _get({ action: "bicepAdminSuggestMentors", requestId }),
  adminRunDaily:           () => _post({ action: "bicepAdminRunDaily" }),
  getMyReportStatus:       () => _get({ action: "bicepGetMyReportStatus" }),
  adminReportCompliance:   () => _get({ action: "bicepAdminReportCompliance" }),
  adminRemindReports:      (incubateeIds) => _post({ action: "bicepAdminRemindReports", incubateeIds }),
  confirmFollow:           () => _post({ action: "confirmFollow" }),
  getMyOverview:           () => _get({ action: "bicepGetMyOverview" }),
  adminListContacts:       (q) => _get({ action: "bicepAdminListContacts", q }),
  // ── Participants (interest registration) ──────────────────────────────────
  registerParticipant:     (fields) => _post({ action: "bicepRegisterParticipant", ...fields }),
  getMyParticipant:        () => _get({ action: "bicepGetMyParticipant" }),
  withdrawParticipant:     () => _post({ action: "bicepWithdrawParticipant" }),
  adminListParticipants:   (status) => _get({ action: "bicepAdminListParticipants", status }),
  adminUpdateParticipant:  (participantId, fields) => _post({ action: "bicepAdminUpdateParticipant", participantId, ...fields }),

  // ── Incubatees & showcase ─────────────────────────────────────────────────
  listIncubatees:          () => _get({ action: "bicepListIncubatees" }),
  getShowcase:             () => _get({ action: "bicepGetShowcase" }),
  getMyIncubatee:          () => _get({ action: "bicepGetMyIncubatee" }),
  updateMyIncubatee:       (incubateeId, fields) => _post({ action: "bicepUpdateMyIncubatee", incubateeId, ...fields }),

  // ── Events ────────────────────────────────────────────────────────────────
  listEvents:              () => _get({ action: "bicepListEvents" }),

  // ── Space requests ────────────────────────────────────────────────────────
  requestSpace:            (fields) => _post({ action: "bicepRequestSpace", ...fields }),
  getMySpaceRequests:      () => _get({ action: "bicepGetMySpaceRequests" }),
  cancelSpaceRequest:      (requestId) => _post({ action: "bicepCancelSpaceRequest", requestId }),

  // ── Mentors, mentorship & sessions ────────────────────────────────────────
  listMentors:             (domain) => _get({ action: "bicepListMentors", domain }),
  requestMentorship:       (fields) => _post({ action: "bicepRequestMentorship", ...fields }),
  getMyMentorshipRequests: () => _get({ action: "bicepGetMyMentorshipRequests" }),
  cancelMentorshipRequest: (requestId) => _post({ action: "bicepCancelMentorshipRequest", requestId }),
  bookSession:             (fields) => _post({ action: "bicepBookSession", ...fields }),
  listMySessions:          () => _get({ action: "bicepListMySessions" }),
  cancelSession:           (sessionId) => _post({ action: "bicepCancelSession", sessionId }),

  // ── Problem statements ────────────────────────────────────────────────────
  listProblems:            (p = {}) => _get({ action: "bicepListProblems", domain: p.domain, source: p.source, all: p.all ? 1 : undefined }),
  requestProblem:          (fields) => _post({ action: "bicepRequestProblem", ...fields }),
  getMyProblemRequests:    () => _get({ action: "bicepGetMyProblemRequests" }),
  withdrawProblemRequest:  (requestId) => _post({ action: "bicepWithdrawProblemRequest", requestId }),

  // ── Hosted events ─────────────────────────────────────────────────────────
  listHostedEvents:        (all) => _get({ action: "bicepListHostedEvents", all: all ? 1 : undefined }),
  registerEvent:           (fields) => _post({ action: "bicepRegisterEvent", ...fields }),
  cancelEventRegistration: (eventId) => _post({ action: "bicepCancelEventRegistration", eventId }),

  // ── Mentor workspace ──────────────────────────────────────────────────────
  mentorGetMentees:        () => _get({ action: "bicepMentorGetMentees" }),
  mentorGetMenteeDetail:   (requestId) => _get({ action: "bicepMentorGetMenteeDetail", requestId }),
  mentorRespond:           (requestId, decision, note) => _post({ action: "bicepMentorRespond", requestId, decision, note }),
  mentorAddNote:           (requestId, note, reportId) => _post({ action: "bicepMentorAddNote", requestId, note, reportId }),
  listMentorNotes:         (incubateeId) => _get({ action: "bicepListMentorNotes", incubateeId }),

  // ── Resources ─────────────────────────────────────────────────────────────
  listResources:           (category) => _get({ action: "bicepListResources", category }),

  // ── Startup hub: milestones, metrics, reports, funding, IP ────────────────
  listMilestones:          (incubateeId) => _get({ action: "bicepListMilestones", incubateeId }),
  addMilestone:            (fields) => _post({ action: "bicepAddMilestone", ...fields }),
  updateMilestone:         (milestoneId, fields) => _post({ action: "bicepUpdateMilestone", milestoneId, ...fields }),
  getStartupMetrics:       (incubateeId) => _get({ action: "bicepGetStartupMetrics", incubateeId }),
  logMetrics:              (fields) => _post({ action: "bicepLogMetrics", ...fields }),
  getMyProgressReports:    (incubateeId) => _get({ action: "bicepGetMyProgressReports", incubateeId }),
  submitProgressReport:    (fields) => _post({ action: "bicepSubmitProgressReport", ...fields }),
  listMyFunding:           () => _get({ action: "bicepListMyFunding" }),
  logFunding:              (fields) => _post({ action: "bicepLogFunding", ...fields }),
  listMyIPFilings:         () => _get({ action: "bicepListMyIPFilings" }),
  logIPFiling:             (fields) => _post({ action: "bicepLogIPFiling", ...fields }),

  // ── Team board & peer help ────────────────────────────────────────────────
  listTeamListings:        (domain) => _get({ action: "bicepListTeamListings", domain }),
  getMyTeamListings:       () => _get({ action: "bicepGetMyTeamListings" }),
  postTeamListing:         (fields) => _post({ action: "bicepPostTeamListing", ...fields }),
  updateTeamListing:       (listingId, is_open) => _post({ action: "bicepUpdateTeamListing", listingId, is_open }),
  expressInterest:         (listingId, message) => _post({ action: "bicepExpressInterest", listingId, message }),
  getListingInterests:     (listingId) => _get({ action: "bicepGetListingInterests", listingId }),
  listPeerRequests:        () => _get({ action: "bicepListPeerRequests" }),
  postPeerRequest:         (fields) => _post({ action: "bicepPostPeerRequest", ...fields }),
  respondPeerRequest:      (requestId, message) => _post({ action: "bicepRespondPeerRequest", requestId, message }),
  getPeerResponses:        (requestId) => _get({ action: "bicepGetPeerResponses", requestId }),

  // ── Competitions & schemes ────────────────────────────────────────────────
  listCompetitions:        (all) => _get({ action: "bicepListCompetitions", all: all ? 1 : undefined }),
  registerCompetition:     (fields) => _post({ action: "bicepRegisterCompetition", ...fields }),
  getMyCompetitionEntries: () => _get({ action: "bicepGetMyCompetitionEntries" }),
  listSchemes:             (all) => _get({ action: "bicepListSchemes", all: all ? 1 : undefined }),
  applyToScheme:           (fields) => _post({ action: "bicepApplyToScheme", ...fields }),
  getMySchemeApplications: () => _get({ action: "bicepGetMySchemeApplications" }),

  // ── Community ─────────────────────────────────────────────────────────────
  listMyCertificates:      () => _get({ action: "bicepListMyCertificates" }),
  listNewsletters:         () => _get({ action: "bicepListNewsletters" }),
  listAlumni:              () => _get({ action: "bicepListAlumni" }),
  listPartners:            () => _get({ action: "bicepListPartners" }),
  expressInvestorInterest: (fields) => _post({ action: "bicepExpressInvestorInterest", ...fields }),

  // ═════════════════════════ COORDINATOR / ADMIN ═══════════════════════════
  adminGetAnalytics:       () => _get({ action: "bicepAdminGetAnalytics" }),

  // Applications
  adminListApplications:   (status) => _get({ action: "bicepAdminListApplications", status }),
  adminGetApplication:     (applicationId) => _get({ action: "bicepAdminGetApplication", applicationId }),
  adminUpdateStatus:       (applicationId, status, note) => _post({ action: "bicepAdminUpdateStatus", applicationId, status, note }),
  adminAddNote:            (applicationId, note) => _post({ action: "bicepAdminAddNote", applicationId, note }),
  adminSetIncubatee:       (applicationId, cohort) => _post({ action: "bicepAdminSetIncubatee", applicationId, cohort }),
  adminSubmitEvaluation:   (fields) => _post({ action: "bicepAdminSubmitEvaluation", ...fields }),
  getEvaluations:          (targetId) => _get({ action: "bicepGetEvaluations", targetId }),

  // Incubatees
  adminListIncubatees:     (status) => _get({ action: "bicepAdminListIncubatees", status }),
  adminUpdateIncubatee:    (incubateeId, fields) => _post({ action: "bicepUpdateMyIncubatee", incubateeId, ...fields }),
  adminGraduateIncubatee:  (incubateeId) => _post({ action: "bicepAdminGraduateIncubatee", incubateeId }),
  adminListMilestones:     (incubateeId, status) => _get({ action: "bicepAdminListMilestones", incubateeId, status }),
  adminGetAllMetrics:      (incubateeId) => _get({ action: "bicepAdminGetAllMetrics", incubateeId }),

  // Space, mentorship & sessions
  adminListSpaceRequests:  (status) => _get({ action: "bicepAdminListSpaceRequests", status }),
  adminUpdateSpaceRequest: (requestId, status, note, approvedSpace) => _post({ action: "bicepAdminUpdateSpaceRequest", requestId, status, note, approvedSpace }),
  adminListMentorshipRequests: (status) => _get({ action: "bicepAdminListMentorshipRequests", status }),
  adminAssignMentor:       (requestId, { mentorId, assignedMentor, status, note } = {}) => _post({ action: "bicepAdminAssignMentor", requestId, mentorId, assignedMentor, status, note }),
  adminAddMentor:          (fields) => _post({ action: "bicepAdminAddMentor", ...fields }),
  adminBulkAddMentors:     (items, mode) => _post({ action: "bicepAdminBulkAddMentors", items, mode }),
  adminUpdateMentor:       (mentorId, fields) => _post({ action: "bicepAdminUpdateMentor", mentorId, ...fields }),
  adminListSessions:       (status, dateFrom, dateTo) => _get({ action: "bicepAdminListSessions", status, dateFrom, dateTo }),
  adminUpdateSession:      (sessionId, status, coordinatorNote) => _post({ action: "bicepAdminUpdateSession", sessionId, status, coordinatorNote }),

  // People, landing settings
  adminListStaff:          () => _get({ action: "bicepAdminListStaff" }),
  adminAddStaff:           (email, name) => _post({ action: "bicepAdminAddStaff", email, name }),
  adminRemoveStaff:        (email) => _post({ action: "bicepAdminRemoveStaff", email }),
  adminSaveSettings:       (settings) => _post({ action: "bicepAdminSaveSettings", settings }),

  // Problem statements & hosted events (coordinators)
  adminAddProblem:         (fields) => _post({ action: "bicepAdminAddProblem", ...fields }),
  adminBulkAddProblems:    (items) => _post({ action: "bicepAdminBulkAddProblems", items }),
  adminUpdateProblem:      (problemId, fields) => _post({ action: "bicepAdminUpdateProblem", problemId, ...fields }),
  adminListProblemRequests: (problemId, status) => _get({ action: "bicepAdminListProblemRequests", problemId, status }),
  adminUpdateProblemRequest: (requestId, status, note) => _post({ action: "bicepAdminUpdateProblemRequest", requestId, status, note }),
  adminAddEvent:           (fields) => _post({ action: "bicepAdminAddEvent", ...fields }),
  adminUpdateEvent:        (eventId, fields) => _post({ action: "bicepAdminUpdateEvent", eventId, ...fields }),
  adminListEventRegistrations: (eventId) => _get({ action: "bicepAdminListEventRegistrations", eventId }),
  adminUpdateEventRegistration: (regId, status) => _post({ action: "bicepAdminUpdateEventRegistration", regId, status }),

  // Resources, competitions, schemes
  adminAddResource:        (fields) => _post({ action: "bicepAdminAddResource", ...fields }),
  adminUpdateResource:     (resourceId, fields) => _post({ action: "bicepAdminUpdateResource", resourceId, ...fields }),
  adminDeleteResource:     (resourceId) => _post({ action: "bicepAdminDeleteResource", resourceId }),
  adminAddCompetition:     (fields) => _post({ action: "bicepAdminAddCompetition", ...fields }),
  adminUpdateCompetition:  (competitionId, fields) => _post({ action: "bicepAdminUpdateCompetition", competitionId, ...fields }),
  adminListCompetitionEntries: (competitionId) => _get({ action: "bicepAdminListCompetitionEntries", competitionId }),
  adminUpdateCompetitionEntry: (entryId, fields) => _post({ action: "bicepAdminUpdateCompetitionEntry", entryId, ...fields }),
  adminAddScheme:          (fields) => _post({ action: "bicepAdminAddScheme", ...fields }),
  adminUpdateScheme:       (schemeId, fields) => _post({ action: "bicepAdminUpdateScheme", schemeId, ...fields }),
  adminListSchemeApplications: (schemeId, status) => _get({ action: "bicepAdminListSchemeApplications", schemeId, status }),
  adminUpdateSchemeApplication: (appId, fields) => _post({ action: "bicepAdminUpdateSchemeApplication", appId, ...fields }),

  // Funding, IP, investors, progress
  adminListFunding:        (status) => _get({ action: "bicepAdminListFunding", status }),
  adminUpdateFunding:      (fundingId, fields) => _post({ action: "bicepAdminUpdateFunding", fundingId, ...fields }),
  adminListIPFilings:      (status) => _get({ action: "bicepAdminListIPFilings", status }),
  adminUpdateIPFiling:     (filingId, fields) => _post({ action: "bicepAdminUpdateIPFiling", filingId, ...fields }),
  adminListInvestorInterests: (incubateeId, status) => _get({ action: "bicepAdminListInvestorInterests", incubateeId, status }),
  adminUpdateInvestorInterest: (interestId, fields) => _post({ action: "bicepAdminUpdateInvestorInterest", interestId, ...fields }),
  adminListProgressReports: (incubateeId, status) => _get({ action: "bicepAdminListProgressReports", incubateeId, status }),
  adminReviewProgressReport: (reportId, rating, note) => _post({ action: "bicepAdminReviewProgressReport", reportId, rating, note }),

  // Community content
  adminAddAlumni:          (fields) => _post({ action: "bicepAdminAddAlumni", ...fields }),
  adminUpdateAlumni:       (alumniId, fields) => _post({ action: "bicepAdminUpdateAlumni", alumniId, ...fields }),
  adminAddPartner:         (fields) => _post({ action: "bicepAdminAddPartner", ...fields }),
  adminUpdatePartner:      (partnerId, fields) => _post({ action: "bicepAdminUpdatePartner", partnerId, ...fields }),
  adminUpdateShowcase:     (incubateeId, fields) => _post({ action: "bicepAdminUpdateShowcase", incubateeId, ...fields }),
  adminIssueCertificate:   (fields) => _post({ action: "bicepAdminIssueCertificate", ...fields }),
  adminListCertificates:   (status) => _get({ action: "bicepAdminListCertificates", status }),
  revokeCertificate:       (certId) => _post({ action: "bicepRevokeCertificate", certId }),
  adminSendNewsletter:     (fields) => _post({ action: "bicepAdminSendNewsletter", ...fields }),
};

// ── Role context ──────────────────────────────────────────────────────────────
// BicepGuard fetches bicepGetInfo once and provides it here, so the shell, the
// pages and the coordinator gate all share one request and one source of truth.
export const BicepContext = createContext({ enabled: false, isCoordinator: false, isBicepAdmin: false, isMentor: false, loading: true });

/** Fetches BICEP info for the signed-in user (used by BicepGuard). */
export function useBicepInfo() {
  const { institute } = useInstitute();
  const { user } = useAuth();
  const [state, setState] = useState({ enabled: false, isCoordinator: false, isBicepAdmin: false, isMentor: false, loading: true, error: null });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    // Don't gate on the institute list having loaded: the request itself carries
    // the stored institute slug, and a failed list must not strand the user.
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    bicepApi.getInfo()
      .then((r) => {
        if (cancelled) return;
        // The Worker is the source of truth for roles (it knows platform super-admins too).
        setState({ enabled: !!r?.enabled, isCoordinator: !!r?.isCoordinator, isBicepAdmin: !!r?.isBicepAdmin, isMentor: !!r?.isMentor, loading: false, error: null });
      })
      .catch((error) => { if (!cancelled) setState({ enabled: false, isCoordinator: false, isBicepAdmin: false, isMentor: false, loading: false, error }); });
    return () => { cancelled = true; };
  }, [institute?.slug, user?.email, nonce]);

  return { ...state, retry: () => setNonce((n) => n + 1) };
}

/** Returns: enabled, isBicepAdmin, isCoordinator (admins included), isMentor, loading (inside <BicepGuard>). */
export function useBicep() { return useContext(BicepContext); }
